import { Fragment } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { when } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { navigate } from '../router.js';
import { Avatar, ConfirmDialog, LoadError, Loading, MoreOptions, Pill, toast } from '../ui.js';
import { ContactDialog, monthLabel, StageBar, type EditableContact, type ProjectSummary } from './Projects.js';
import { ClientFactsBand, type ClientFacts } from './Clients.js';
import { ClientFilms } from './Archive.js';
import { ClientStartBlock, type ClientStartView } from './clientStart.js';
import { ClientBillingBlock } from './billing.js';
import { ClientBrandBlock } from './brand.js';
import { InviteButton, PortalAccessLine, PortalInviteSuggestion, type PortalAccess } from './portalInvite.js';

/**
 * Notes on projects and clients (owner request 2026-10-15): short internal notes on a project (optionally about one
 * video) or on the client itself, gathered on the client card. Employees only; @-named colleagues see them in My Work.
 */

export const NOTE_KINDS = ['decision', 'client_request', 'problem', 'lesson', 'internal'] as const;
export type NoteKind = (typeof NOTE_KINDS)[number];
export const NOTE_KIND_LABEL: Record<NoteKind, string> = { decision: 'Decision', client_request: 'Client request', problem: 'Problem', lesson: 'Lesson learned', internal: 'Internal' };
const KIND_TONE: Record<NoteKind, 'ok' | 'info' | 'warn' | 'crit' | 'neutral'> = { decision: 'ok', client_request: 'info', problem: 'crit', lesson: 'warn', internal: 'neutral' };
const NOTE_MAX = 4000;

const TX = texts(
  {
    kind: NOTE_KIND_LABEL,
    placeholder: 'Write a note… type @ to name a colleague',
    colleagues: 'Colleagues',
    added: 'Note added',
    addedSeen: (names: string) => `Note added · ${names} will see it in My work`,
    newNote: 'New note',
    typeOfNote: 'Type of note',
    note: 'Note',
    about: 'About',
    wholeProject: 'The whole project',
    theClient: 'The client',
    pin: 'Pin to top',
    left: (n: number) => `${n} left`,
    adding: 'Adding…',
    addNote: 'Add note',
    edited: ' · edited',
    fromMonday: ' · from Monday',
    company: 'Company details',
    orgNo: 'Org. no',
    address: 'Address',
    invoice: 'Invoicing',
    monday: 'Agreement in Monday',
    earlier: (n: number) => `Earlier projects (Monday) · ${n}`,
    openDrive: 'Open the project folder in Drive',
    nVideos: (n: number) => `${n} ${n === 1 ? 'video' : 'videos'}`,
    mPackage: 'Package',
    mMrr: 'Per month',
    mValue: 'Agreement value',
    mPeriod: 'Period',
    mNotice: 'Notice',
    months: (n: number) => `${n} ${n === 1 ? 'month' : 'months'}`,
    mFiles: 'Files',
    mWaiting: 'being copied to Drive',
    mFailed: 'could not be copied, open it in Monday',
    mNoteFile: 'from a note',
    lifecycle: { prospect: 'Prospect', inactive: 'Former client', out: 'Former client', paused: 'Paused' } as Record<string, string>,
    onClient: 'On the client',
    video: (name: string) => `Video: ${name}`,
    pinned: 'Pinned',
    pinBtn: 'Pin',
    edit: 'Edit',
    del: 'Delete',
    editNote: 'Edit note',
    save: 'Save',
    cancel: 'Cancel',
    delTitle: 'Delete this note?',
    delBody: 'It disappears for everyone, on the project and on the client card.',
    delConfirm: 'Delete note',
    deleted: 'Note deleted',
    type: 'Type',
    all: 'All',
    from: 'From',
    everything: 'Everything',
    clientItself: 'The client itself',
    pinnedNotes: 'Pinned notes',
    otherNotes: 'Other notes',
    notes: 'Notes',
    latest: 'Latest',
    noMatch: 'No notes match the filter.',
    lessonsHead: 'The project is delivered – what did we learn?',
    lessonsBody: "Two or three lessons learned (what worked, what didn't, what to think of next time) end up on the client card and in the next brief.",
    writeLessons: 'Write lessons learned',
    clientBrief: 'Client brief',
    internal: 'Internal',
    emptyProject: 'No notes yet. Decisions, client requests, problems and lessons learned written here also show on the client card.',
    version: (v: number) => `Version ${v}`,
    someone: 'Someone',
    replaceDraft: 'Replace your text with a new draft from Klara?',
    replaceBody: 'What you have written here and not saved is lost.',
    replaceOk: 'Replace with new draft',
    klaraDraft: (notes: number, projects: number) => `Klara's draft from ${notes} notes and ${projects} projects – read it, fix what is wrong, then save.`,
    briefSaved: (v: number) => `Client brief saved (version ${v})`,
    klaraWriting: 'Klara is writing…',
    updateKlara: 'Update with Klara',
    draftKlara: 'Draft with Klara',
    write: 'Write',
    saving: 'Saving…',
    saveBrief: 'Save brief',
    newDraftKlara: 'New draft with Klara',
    noBrief: 'No client brief yet. It sums up who the client is, what matters to them and the lessons from earlier projects – the team reads it before every new project.',
    letKlara: ' Let Klara draft it from the notes below, or write it yourself.',
    projects: (n: number) => `${n} ${n === 1 ? 'project' : 'projects'}`,
    backClients: '← Clients',
    client: 'Client',
    nOpen: (n: number) => ` (${n} open)`,
    nNotes: (n: number) => `${n} ${n === 1 ? 'note' : 'notes'}`,
    emptyClient: 'No notes yet. Write what everyone should know about this client – notes written on its projects show here too.',
    projectsContacts: 'Projects and contacts',
    projectsHead: 'Projects',
    noProjects: 'No projects yet.',
    contacts: 'Contacts',
    editContact: 'Edit',
    editContactFor: (n: string) => `Edit ${n}`,
    noContacts: 'No contacts yet.',
    driveTitle: 'Drive folders',
    driveReady: '{Client} - General with the client folder and the internal folder.',
    driveOpen: 'Open in Drive',
    driveWait: 'Made when a quote is signed or the first project gets its folders. An existing folder in Drive is used.',
    driveMake: 'Create folders',
    driveMaking: 'Creating…',
    addContact: 'Add contact',
    mailTitle: 'Emails to the client',
    mailEnglish: 'In English',
    mailHint: 'Review emails and reminders, delivery, the project brief, the portal and its sign-in code, and shoot invites. Swedish when unticked.',
    mailSaved: (en: boolean): string => (en ? 'The client now gets emails in English.' : 'The client now gets emails in Swedish.'),
    mailHow: 'Review emails, reminders and delivery',
    mailOpt: { on: 'On', no_reminders: 'No reminders', off: 'Off – the producer sends them' } as Record<ClientMailLevel, string>,
    mailHowHint: 'Applies to all the client’s projects; a project can be stricter. The brief and shoot invites are always sent by the producer.',
    mailHowSaved: 'Saved',
    mentioned: 'Mentioned in notes'
  },
  {
    kind: { decision: 'Beslut', client_request: 'Kundönskemål', problem: 'Problem', lesson: 'Lärdom', internal: 'Intern' },
    placeholder: 'Skriv en anteckning… skriv @ för att nämna en kollega',
    colleagues: 'Kollegor',
    added: 'Anteckningen sparad',
    addedSeen: (names: string) => `Anteckningen sparad · ${names} ser den i Mitt arbete`,
    newNote: 'Ny anteckning',
    typeOfNote: 'Typ av anteckning',
    note: 'Anteckning',
    about: 'Gäller',
    wholeProject: 'Hela projektet',
    theClient: 'Kunden',
    pin: 'Fäst överst',
    left: (n: number) => `${n} tecken kvar`,
    adding: 'Lägger till…',
    addNote: 'Lägg till anteckning',
    edited: ' · ändrad',
    fromMonday: ' · från Monday',
    company: 'Företagsuppgifter',
    orgNo: 'Org.nr',
    address: 'Adress',
    invoice: 'Fakturering',
    monday: 'Avtal i Monday',
    earlier: (n: number) => `Tidigare projekt (Monday) · ${n}`,
    openDrive: 'Öppna projektmappen i Drive',
    nVideos: (n: number) => `${n} ${n === 1 ? 'video' : 'videor'}`,
    mPackage: 'Paket',
    mMrr: 'Per månad',
    mValue: 'Avtalsvärde',
    mPeriod: 'Period',
    mNotice: 'Uppsägning',
    months: (n: number) => `${n} ${n === 1 ? 'månad' : 'månader'}`,
    mFiles: 'Filer',
    mWaiting: 'kopieras till Drive',
    mFailed: 'gick inte att kopiera, öppna den i Monday',
    mNoteFile: 'från en anteckning',
    lifecycle: { prospect: 'Potentiell kund', inactive: 'Tidigare kund', out: 'Tidigare kund', paused: 'Pausad' } as Record<string, string>,
    onClient: 'På kunden',
    video: (name: string) => `Video: ${name}`,
    pinned: 'Fäst',
    pinBtn: 'Fäst',
    edit: 'Ändra',
    del: 'Ta bort',
    editNote: 'Ändra anteckning',
    save: 'Spara',
    cancel: 'Avbryt',
    delTitle: 'Ta bort anteckningen?',
    delBody: 'Den försvinner för alla, både på projektet och på kundkortet.',
    delConfirm: 'Ta bort anteckning',
    deleted: 'Anteckningen borttagen',
    type: 'Typ',
    all: 'Alla',
    from: 'Från',
    everything: 'Allt',
    clientItself: 'Kunden själv',
    pinnedNotes: 'Fästa anteckningar',
    otherNotes: 'Övriga anteckningar',
    notes: 'Anteckningar',
    latest: 'Senaste',
    noMatch: 'Inga anteckningar matchar filtret.',
    lessonsHead: 'Projektet är levererat – vad lärde vi oss?',
    lessonsBody: 'Två eller tre lärdomar (vad som funkade, vad som inte gjorde det, vad vi ska tänka på nästa gång) hamnar på kundkortet och i nästa brief.',
    writeLessons: 'Skriv lärdomar',
    clientBrief: 'Kundbrief',
    internal: 'Intern',
    emptyProject: 'Inga anteckningar än. Beslut, kundönskemål, problem och lärdomar som skrivs här syns också på kundkortet.',
    version: (v: number) => `Version ${v}`,
    someone: 'Någon',
    replaceDraft: 'Ersätta din text med ett nytt utkast från Klara?',
    replaceBody: 'Det du har skrivit här och inte sparat försvinner.',
    replaceOk: 'Ersätt med nytt utkast',
    klaraDraft: (notes: number, projects: number) => `Klaras utkast utifrån ${notes} anteckningar och ${projects} projekt – läs det, rätta det som är fel och spara sedan.`,
    briefSaved: (v: number) => `Kundbriefen sparad (version ${v})`,
    klaraWriting: 'Klara skriver…',
    updateKlara: 'Uppdatera med Klara',
    draftKlara: 'Skriv utkast med Klara',
    write: 'Skriv',
    saving: 'Sparar…',
    saveBrief: 'Spara brief',
    newDraftKlara: 'Nytt utkast med Klara',
    noBrief: 'Ingen kundbrief än. Den sammanfattar vem kunden är, vad som är viktigt för dem och lärdomarna från tidigare projekt – teamet läser den före varje nytt projekt.',
    letKlara: ' Låt Klara skriva ett utkast utifrån anteckningarna nedan, eller skriv den själv.',
    projects: (n: number) => `${n} projekt`,
    backClients: '← Kunder',
    client: 'Kund',
    nOpen: (n: number) => ` (${n} öppna)`,
    nNotes: (n: number) => `${n} ${n === 1 ? 'anteckning' : 'anteckningar'}`,
    emptyClient: 'Inga anteckningar än. Skriv det alla bör veta om kunden – anteckningar från kundens projekt syns också här.',
    projectsContacts: 'Projekt och kontakter',
    projectsHead: 'Projekt',
    noProjects: 'Inga projekt än.',
    contacts: 'Kontakter',
    editContact: 'Ändra',
    editContactFor: (n: string) => `Ändra ${n}`,
    noContacts: 'Inga kontakter än.',
    driveTitle: 'Drive-mappar',
    driveReady: '{Kund} - General med kundmappen och den interna mappen.',
    driveOpen: 'Öppna i Drive',
    driveWait: 'Skapas när en offert signeras eller första projektet får sina mappar. En mapp som redan finns i Drive används.',
    driveMake: 'Skapa mappar',
    driveMaking: 'Skapar…',
    addContact: 'Lägg till kontakt',
    mailTitle: 'Utskick till kunden',
    mailEnglish: 'På engelska',
    mailHint: 'Granskningsmejl och påminnelser, leverans, projektbriefen, portalen och dess inloggningskod samt inspelningsinbjudan. Svenska när rutan är tom.',
    mailSaved: (en: boolean) => (en ? 'Kunden får nu utskick på engelska.' : 'Kunden får nu utskick på svenska.'),
    mailHow: 'Granskningsmejl, påminnelser och leverans',
    mailOpt: { on: 'På', no_reminders: 'Inga påminnelser', off: 'Av – producenten skickar själv' } as Record<ClientMailLevel, string>,
    mailHowHint: 'Gäller alla kundens projekt; ett projekt kan vara strängare. Brief och inspelningsinbjudan skickar producenten alltid själv.',
    mailHowSaved: 'Sparat',
    mentioned: 'Omnämnd i anteckningar'
  }
);

export interface NoteView {
  id: string;
  kind: NoteKind;
  body: string;
  pinned: boolean;
  author: { id: string; name: string };
  mentions: { id: string; name: string }[];
  createdAt: string;
  editedAt: string | null;
  client: { id: string; name: string; code: string };
  project: { id: string; name: string; code: string } | null;
  video: { id: string; name: string } | null;
  /** from Monday's Client Notes: read only, signed as in Monday */
  fromMonday?: boolean;
  can: { edit: boolean; delete: boolean };
}
interface Person {
  id: string;
  name: string;
}

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const go = (path: string) => (e: Event) => (e.preventDefault(), navigate(path));
const val = (e: Event) => (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Pinned first, then newest first (the API sends them like that; kept after local changes). */
const ordered = (notes: NoteView[]) => [...notes].sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.createdAt.localeCompare(a.createdAt));

/** The note's text with @-named colleagues marked. */
export function NoteBody(p: { body: string; mentions: Person[] }) {
  if (!p.mentions.length) return <p class="note-body">{p.body}</p>;
  const names = [...p.mentions].map((m) => m.name).sort((a, b) => b.length - a.length);
  const parts = p.body.split(new RegExp(`(@(?:${names.map(escapeRe).join('|')}))(?!\\p{L})`, 'giu'));
  return (
    <p class="note-body">
      {parts.map((part, i) => (i % 2 ? <span class="mention">{part}</span> : part))}
    </p>
  );
}

/** "@Pi" before the caret → the people whose name (or a word of it) starts with "Pi". */
function mentionQuery(text: string, caret: number): { start: number; q: string } | null {
  const m = /(^|\s)@([\p{L}-]*)$/u.exec(text.slice(0, caret));
  return m ? { start: caret - m[2]!.length - 1, q: m[2]!.toLocaleLowerCase('sv') } : null;
}

/** A textarea that suggests colleagues after "@". */
function MentionInput(p: { id: string; value: string; onInput: (v: string) => void; people: Person[]; onSubmit: () => void; label: string; autoFocus?: boolean }) {
  const t = TX[useLang()];
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (p.autoFocus) ref.current?.focus();
  }, []);
  const [q, setQ] = useState<{ start: number; q: string } | null>(null);
  const [active, setActive] = useState(0);
  const hits = useMemo(
    () =>
      q
        ? p.people
            .filter((x) => {
              const n = x.name.toLocaleLowerCase('sv');
              return !q.q || n.startsWith(q.q) || n.split(/\s+/).some((w) => w.startsWith(q.q));
            })
            .slice(0, 6)
        : [],
    [q, p.people]
  );
  const update = (text: string) => {
    p.onInput(text);
    const el = ref.current;
    setQ(el ? mentionQuery(text, el.selectionStart ?? text.length) : null);
    setActive(0);
  };
  const pick = (x: Person) => {
    if (!q) return;
    const el = ref.current;
    const caret = el?.selectionStart ?? p.value.length;
    const next = `${p.value.slice(0, q.start)}@${x.name} ${p.value.slice(caret)}`;
    p.onInput(next);
    setQ(null);
    const pos = q.start + x.name.length + 2;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(pos, pos);
    });
  };
  const open = hits.length > 0;
  return (
    <div class="mention-wrap">
      <textarea
        ref={ref}
        id={p.id}
        class="textarea note-input"
        value={p.value}
        maxLength={NOTE_MAX}
        placeholder={t.placeholder}
        aria-label={p.label}
        aria-autocomplete="list"
        aria-controls={open ? `${p.id}-people` : undefined}
        aria-activedescendant={open ? `${p.id}-p${active}` : undefined}
        onInput={(e) => update(val(e))}
        onBlur={() => setTimeout(() => setQ(null), 150)}
        onKeyDown={(e) => {
          if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setActive((active + (e.key === 'ArrowDown' ? 1 : hits.length - 1)) % hits.length);
          } else if (open && (e.key === 'Enter' || e.key === 'Tab')) {
            e.preventDefault();
            pick(hits[active]!);
          } else if (open && e.key === 'Escape') {
            e.preventDefault();
            setQ(null);
          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            p.onSubmit();
          }
        }}
      />
      {open ? (
        <ul class="mention-list" role="listbox" id={`${p.id}-people`} aria-label={t.colleagues}>
          {hits.map((x, i) => (
            <li id={`${p.id}-p${i}`} role="option" aria-selected={i === active} class={i === active ? 'is-active' : undefined} onMouseDown={(e) => (e.preventDefault(), pick(x))}>
              <Avatar name={x.name} size="sm" decorative /> {x.name}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function KindPicker(p: { value: NoteKind; onChange: (k: NoteKind) => void; label: string }) {
  const t = TX[useLang()];
  return (
    <div class="cluster note-kinds" role="radiogroup" aria-label={p.label}>
      {NOTE_KINDS.map((k) => (
        <button type="button" role="radio" aria-checked={p.value === k} class={`btn btn-sm note-kind note-kind--${k}${p.value === k ? ' is-on' : ''}`} onClick={() => p.onChange(k)}>
          {t.kind[k]}
        </button>
      ))}
    </div>
  );
}

type Target = { projectId: string } | { clientId: string; projects: { id: string; name: string; code: string }[] };

/** Write a note: type, text with @-names, the video (project) or what it is about (client card), pin. */
function Composer(p: { api: Api; target: Target; people: Person[]; videos?: Person[]; initialKind?: NoteKind; autoFocus?: boolean; onAdded: (n: NoteView) => void }) {
  const t = TX[useLang()];
  const [kind, setKind] = useState<NoteKind>(p.initialKind ?? 'internal');
  const [body, setBody] = useState('');
  const [videoId, setVideoId] = useState('');
  const [about, setAbout] = useState('');
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  const id = 'projectId' in p.target ? `nc-${p.target.projectId}` : `nc-${p.target.clientId}`;
  const submit = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      const where = 'projectId' in p.target ? { projectId: p.target.projectId, videoId: videoId || null } : about ? { projectId: about } : { clientId: p.target.clientId };
      const r = await p.api.post<{ note: NoteView }>('/api/notes', { ...where, kind, body, pinned });
      p.onAdded(r.note);
      setBody('');
      setPinned(false);
      setVideoId('');
      toast({ text: r.note.mentions.length ? t.addedSeen(r.note.mentions.map((m) => m.name).join(', ')) : t.added });
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      class="blk note-compose"
      aria-label={t.newNote}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <MentionInput id={id} value={body} onInput={setBody} people={p.people} onSubmit={() => void submit()} label={t.note} autoFocus={p.autoFocus} />
      <MoreOptions id="note-compose" filled={(kind !== (p.initialKind ?? 'internal') ? 1 : 0) + (videoId || about ? 1 : 0) + (pinned ? 1 : 0)}>
        <KindPicker value={kind} onChange={setKind} label={t.typeOfNote} />
        <div class="note-compose-foot">
          {'projectId' in p.target && p.videos?.length ? (
            <label class="cluster small">
              <span class="muted">{t.about}</span>
              <select class="input note-select" value={videoId} onChange={(e) => setVideoId(val(e))}>
                <option value="">{t.wholeProject}</option>
                {p.videos.map((v) => (
                  <option value={v.id}>{v.name}</option>
                ))}
              </select>
            </label>
          ) : null}
          {'clientId' in p.target ? (
            <label class="cluster small">
              <span class="muted">{t.about}</span>
              <select class="input note-select" value={about} onChange={(e) => setAbout(val(e))}>
                <option value="">{t.theClient}</option>
                {p.target.projects.map((x) => (
                  <option value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label class="check small">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned((e.target as HTMLInputElement).checked)} /> {t.pin}
          </label>
        </div>
      </MoreOptions>
      <div class="note-compose-foot">
        <span class="topbar-spacer" />
        {body.length > NOTE_MAX - 200 ? <span class="small muted">{t.left(NOTE_MAX - body.length)}</span> : null}
        <button type="submit" class="btn btn-primary" disabled={!body.trim() || busy}>
          {busy ? t.adding : t.addNote}
        </button>
      </div>
    </form>
  );
}

function NoteItem(p: { api: Api; n: NoteView; people: Person[]; showProject: boolean; showClient?: boolean; onChange: (n: NoteView) => void; onDelete: (id: string) => void }) {
  const { n } = p;
  const lang = useLang();
  const t = TX[lang];
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(n.body);
  const [kind, setKind] = useState<NoteKind>(n.kind);
  const [confirm, setConfirm] = useState(false);
  const put = async (patch: Record<string, unknown>) => {
    try {
      p.onChange((await p.api.put<{ note: NoteView }>(`/api/notes/${n.id}`, patch)).note);
      return true;
    } catch (e) {
      toast({ text: errText(e) });
      return false;
    }
  };
  return (
    <li class={`note${n.pinned ? ' note--pinned' : ''}`}>
      <div class="note-head">
        <Avatar name={n.author.name} size="sm" decorative />
        <span class="note-meta">
          <strong>{n.author.name}</strong>
          <span class="small muted">
            {when(n.createdAt, lang)}
            {n.editedAt ? t.edited : ''}
            {n.fromMonday ? t.fromMonday : ''}
          </span>
        </span>
        <Pill tone={KIND_TONE[n.kind]}>{t.kind[n.kind]}</Pill>
        {p.showClient ? (
          <a class="note-where small" href={`/clients/${n.client.id}`} onClick={go(`/clients/${n.client.id}`)}>
            {n.client.name}
          </a>
        ) : null}
        {p.showProject ? (
          n.project ? (
            <a class="note-where small" href={`/projects/${n.project.id}/notes`} onClick={go(`/projects/${n.project.id}/notes`)} title={n.project.code}>
              {n.project.name}
            </a>
          ) : (
            <span class="note-where small muted">{t.onClient}</span>
          )
        ) : null}
        {n.video ? <span class="note-where small muted">{t.video(n.video.name)}</span> : null}
        <span class="note-actions">
        <button type="button" class={`btn btn-ghost btn-sm note-pin${n.pinned ? ' is-on' : ''}`} aria-pressed={n.pinned} onClick={() => void put({ pinned: !n.pinned })}>
          {n.pinned ? t.pinned : t.pinBtn}
        </button>
        {n.can.edit && !editing ? (
          <button type="button" class="btn btn-ghost btn-sm" onClick={() => (setBody(n.body), setKind(n.kind), setEditing(true))}>
            {t.edit}
          </button>
        ) : null}
        {n.can.delete && !editing ? (
          <button type="button" class="btn btn-ghost btn-sm" onClick={() => setConfirm(true)}>
            {t.del}
          </button>
        ) : null}
        </span>
      </div>
      {editing ? (
        <form
          class="stack note-edit"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await put({ body, kind })) setEditing(false);
          }}
        >
          <KindPicker value={kind} onChange={setKind} label={t.typeOfNote} />
          <MentionInput id={`ne-${n.id}`} value={body} onInput={setBody} people={p.people} onSubmit={() => void put({ body, kind }).then((ok) => ok && setEditing(false))} label={t.editNote} />
          <div class="cluster">
            <button type="submit" class="btn btn-primary btn-sm" disabled={!body.trim()}>
              {t.save}
            </button>
            <button type="button" class="btn btn-sm" onClick={() => setEditing(false)}>
              {t.cancel}
            </button>
          </div>
        </form>
      ) : (
        <NoteBody body={n.body} mentions={n.mentions} />
      )}
      {confirm ? (
        <ConfirmDialog
          title={t.delTitle}
          body={t.delBody}
          confirmLabel={t.delConfirm}
          danger
          onClose={() => setConfirm(false)}
          onConfirm={async () => {
            await p.api.del(`/api/notes/${n.id}`);
            p.onDelete(n.id);
            toast({ text: t.deleted });
          }}
        />
      ) : null}
    </li>
  );
}

/** The list of notes with type filters (and, on the client card, a project filter), pinned ones on top. */
function NoteFeed(p: { api: Api; notes: NoteView[]; setNotes: (n: NoteView[]) => void; people: Person[]; showProject: boolean; projects?: { id: string; name: string }[]; empty: string }) {
  const t = TX[useLang()];
  const [kind, setKind] = useState<NoteKind | ''>('');
  const [where, setWhere] = useState('');
  const shown = p.notes.filter((n) => (!kind || n.kind === kind) && (!where || (where === 'client' ? !n.project : n.project?.id === where)));
  const pinned = shown.filter((n) => n.pinned);
  const rest = shown.filter((n) => !n.pinned);
  const replace = (n: NoteView) => p.setNotes(ordered(p.notes.map((x) => (x.id === n.id ? n : x))));
  const remove = (id: string) => p.setNotes(p.notes.filter((x) => x.id !== id));
  const item = (n: NoteView) => <NoteItem api={p.api} n={n} people={p.people} showProject={p.showProject} onChange={replace} onDelete={remove} />;
  const counts = (k: NoteKind) => p.notes.filter((n) => n.kind === k).length;
  return (
    <div class="stack">
      {p.notes.length ? (
        <div class="cluster note-filters">
          <div class="cluster" role="group" aria-label={t.type}>
            <button type="button" class={`btn btn-sm${!kind ? ' btn-primary' : ''}`} aria-pressed={!kind} onClick={() => setKind('')}>
              {t.all} <span class="seg-n">{p.notes.length}</span>
            </button>
            {NOTE_KINDS.filter((k) => counts(k)).map((k) => (
              <button type="button" class={`btn btn-sm${kind === k ? ' btn-primary' : ''}`} aria-pressed={kind === k} onClick={() => setKind(kind === k ? '' : k)}>
                {t.kind[k]} <span class="seg-n">{counts(k)}</span>
              </button>
            ))}
          </div>
          {p.projects?.length ? (
            <label class="cluster small">
              <span class="muted">{t.from}</span>
              <select class="input note-select" value={where} onChange={(e) => setWhere(val(e))}>
                <option value="">{t.everything}</option>
                <option value="client">{t.clientItself}</option>
                {p.projects.map((x) => (
                  <option value={x.id}>{x.name}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}
      {pinned.length ? (
        <section aria-label={t.pinnedNotes}>
          <h3 class="note-group">{t.pinned}</h3>
          <ul class="notes">{pinned.map(item)}</ul>
        </section>
      ) : null}
      {rest.length ? (
        <section aria-label={pinned.length ? t.otherNotes : t.notes}>
          {pinned.length ? <h3 class="note-group">{t.latest}</h3> : null}
          <ul class="notes">{rest.map(item)}</ul>
        </section>
      ) : null}
      {!shown.length ? <p class="muted">{p.notes.length ? t.noMatch : p.empty}</p> : null}
    </div>
  );
}

/** The project's Notes tab. */
export function ProjectNotes(p: { api: Api; id: string }) {
  const [v, setV] = useState<{ notes: NoteView[]; videos: Person[]; people: Person[]; clientBrief: BriefView; askLessons: boolean } | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const t = TX[useLang()];
  const [lessons, setLessons] = useState(false);
  useEffect(() => {
    p.api.get<NonNullable<typeof v>>(`/api/projects/${p.id}/notes`).then(setV, setErr);
  }, [p.id]);
  if (err) return <LoadError error={err} />;
  if (!v) return <Loading />;
  const asked = v.askLessons && !v.notes.some((n) => n.kind === 'lesson');
  return (
    <div class="stack notes-tab">
      {asked && !lessons ? (
        <div class="callout callout--info lessons-ask" role="status">
          <strong>{t.lessonsHead}</strong>
          <div>{t.lessonsBody}</div>
          <div>
            <button type="button" class="btn btn-primary btn-sm" onClick={() => setLessons(true)}>
              {t.writeLessons}
            </button>
          </div>
        </div>
      ) : null}
      {v.clientBrief.body ? (
        <details class="blk brief brief--fold">
          <summary>
            <span class="blk-title">{t.clientBrief}</span> <span class="brief-lock">{t.internal}</span>
          </summary>
          <BriefText b={v.clientBrief} />
        </details>
      ) : null}
      <Composer
        key={lessons ? 'lesson' : 'note'}
        api={p.api}
        target={{ projectId: p.id }}
        people={v.people}
        videos={v.videos}
        initialKind={lessons ? 'lesson' : undefined}
        autoFocus={lessons}
        onAdded={(n) => setV({ ...v, notes: ordered([n, ...v.notes]) })}
      />
      <NoteFeed api={p.api} notes={v.notes} setNotes={(notes) => setV({ ...v, notes })} people={v.people} showProject={false} empty={t.emptyProject} />
    </div>
  );
}

export interface BriefView {
  body: string;
  version: number;
  updatedBy: string | null;
  updatedAt: string | null;
  canEdit: boolean;
  internalLine: string;
}

function BriefText(p: { b: BriefView }) {
  const lang = useLang();
  const t = TX[lang];
  return (
    <>
      <p class="brief-internal">{p.b.internalLine}</p>
      <div class="brief-body">{p.b.body}</div>
      {p.b.updatedAt ? (
        <p class="small muted">
          {t.version(p.b.version)} · {p.b.updatedBy ?? t.someone} · {when(p.b.updatedAt, lang)}
        </p>
      ) : null}
    </>
  );
}

/** The client part of the brief on the client card: internal only; Klara drafts, a producer edits and saves. */
function ClientBriefBlock(p: { api: Api; clientId: string; brief: BriefView; onSaved: (b: BriefView) => void }) {
  const t = TX[useLang()];
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(p.brief.body);
  const [busy, setBusy] = useState<'' | 'draft' | 'save'>('');
  const [note, setNote] = useState('');
  const [ask, setAsk] = useState(false);
  const draft = async (sure = false) => {
    if (!sure && text.trim() && text !== p.brief.body) return setAsk(true);
    setBusy('draft');
    try {
      const r = await p.api.post<{ draft: string; basedOn: { notes: number; projects: number } }>(`/api/clients/${p.clientId}/brief/draft`);
      setText(r.draft);
      setEditing(true);
      setNote(t.klaraDraft(r.basedOn.notes, r.basedOn.projects));
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy('');
    }
  };
  const save = async () => {
    setBusy('save');
    try {
      const r = await p.api.put<{ brief: BriefView }>(`/api/clients/${p.clientId}/brief`, { body: text });
      p.onSaved(r.brief);
      setEditing(false);
      setNote('');
      toast({ text: t.briefSaved(r.brief.version) });
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy('');
    }
  };
  return (
    <section class="blk brief" aria-labelledby="cb-h">
      <div class="blk-head">
        <h2 class="blk-title" id="cb-h">
          {t.clientBrief} <span class="brief-lock">{t.internal}</span>
        </h2>
        {p.brief.canEdit && !editing ? (
          <div class="blk-actions cluster">
            <button type="button" class="btn btn-sm" disabled={busy !== ''} onClick={() => void draft()}>
              {busy === 'draft' ? t.klaraWriting : p.brief.body ? t.updateKlara : t.draftKlara}
            </button>
            <button type="button" class="btn btn-sm" onClick={() => (setText(p.brief.body), setEditing(true))}>
              {p.brief.body ? t.edit : t.write}
            </button>
          </div>
        ) : null}
      </div>
      {editing ? (
        <form
          class="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <p class="brief-internal">{p.brief.internalLine}</p>
          {note ? <p class="small muted">{note}</p> : null}
          <textarea class="textarea brief-input" aria-label={t.clientBrief} value={text} maxLength={8000} onInput={(e) => setText(val(e))} />
          <div class="cluster">
            <button type="submit" class="btn btn-primary btn-sm" disabled={busy !== ''}>
              {busy === 'save' ? t.saving : t.saveBrief}
            </button>
            <button type="button" class="btn btn-sm" disabled={busy !== ''} onClick={() => void draft()}>
              {busy === 'draft' ? t.klaraWriting : t.newDraftKlara}
            </button>
            <button type="button" class="btn btn-sm" onClick={() => (setEditing(false), setNote(''))}>
              {t.cancel}
            </button>
          </div>
        </form>
      ) : p.brief.body ? (
        <BriefText b={p.brief} />
      ) : (
        <p class="muted">
          {t.noBrief}
          {p.brief.canEdit ? t.letKlara : ''}
        </p>
      )}
      {ask ? <ConfirmDialog title={t.replaceDraft} body={<p>{t.replaceBody}</p>} confirmLabel={t.replaceOk} danger onConfirm={() => void draft(true)} onClose={() => setAsk(false)} /> : null}
    </section>
  );
}

interface ClientCardView {
  today?: string;
  facts?: ClientFacts;
  brief: BriefView;
  client: { id: string; name: string; code: string; fromMonday: boolean; language: 'sv' | 'en'; clientMail?: ClientMailLevel; lifecycle?: string; orgNo?: string | null; address?: string | null; invoiceInfo?: string | null };
  contacts: (EditableContact & { portal?: PortalAccess })[];
  /** contacts to invite to the client portal: the client has an agreement and they have no link yet (2026-10-28) */
  portalSuggest?: string[];
  /** a new client's start in five steps; null when the client is not starting (2026-10-28) */
  start?: ClientStartView | null;
  projects: ProjectSummary[];
  notes: NoteView[];
  people: Person[];
  can?: { contacts: boolean; quote: boolean };
  /** what KFF made for the client before the Hub, from Monday's Video Management (0052) */
  history?: HistoryRow[];
  /** the agreement as Monday had it and its files, for those who see agreements (0050) */
  monday?: { contract: MondayContract | null; files: MondayFile[] } | null;
}
interface MondayContract {
  package: string | null;
  mrr: number | null;
  value: number | null;
  terminationMonths: number | null;
  start: string | null;
  end: string | null;
}
interface MondayFile {
  id: string;
  name: string;
  source: 'agreement' | 'note';
  at: string | null;
  url: string | null;
  state: 'copied' | 'waiting' | 'failed';
}
interface HistoryRow {
  id: string;
  name: string;
  month: string | null;
  status: string | null;
  package: string | null;
  videos: number | null;
  producer: string | null;
  crew: string | null;
  driveUrl: string | null;
  ziflowUrl: string | null;
}

/** "Earlier projects": Monday's project history by year, closed until opened; each opens its Drive folder. */
function EarlierProjects(p: { rows: HistoryRow[]; t: (typeof TX)['sv']; lang: 'sv' | 'en' }) {
  const t = p.t;
  const years = [...new Set(p.rows.map((r) => r.month?.slice(0, 4) ?? '–'))];
  return (
    <section class="blk" aria-labelledby="cc-h">
      <details class="cc-history">
        <summary>
          <h2 class="blk-title" id="cc-h">
            {t.earlier(p.rows.length)}
          </h2>
        </summary>
        {years.map((y) => (
          <div key={y}>
            <h3 class="small muted cc-history-year">{y}</h3>
            <ul class="cc-projects">
              {p.rows
                .filter((r) => (r.month?.slice(0, 4) ?? '–') === y)
                .map((r) => (
                  <li key={r.id}>
                    {r.driveUrl ? (
                      <a href={r.driveUrl} target="_blank" rel="noopener noreferrer" title={t.openDrive}>
                        {r.name}
                      </a>
                    ) : (
                      <span>{r.name}</span>
                    )}
                    <span class="small muted">
                      {[r.month ? monthLabel(r.month, p.lang) : null, r.status, r.videos ? t.nVideos(r.videos) : null, r.producer].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </details>
    </section>
  );
}

const sek = (n: number) => `${n.toLocaleString('sv-SE')} kr`;

/** The client card's "Agreement in Monday": the columns All Client Data had, and the files (opened in Drive once copied). */
function MondayAgreement(p: { m: { contract: MondayContract | null; files: MondayFile[] }; t: (typeof TX)['sv'] }) {
  const { contract: c, files } = p.m;
  const t = p.t;
  const rows: [string, string][] = c
    ? ([
        [t.mPackage, c.package],
        [t.mMrr, c.mrr ? sek(c.mrr) : null],
        [t.mValue, c.value ? sek(c.value) : null],
        [t.mPeriod, c.start || c.end ? `${c.start ?? '…'} – ${c.end ?? '…'}` : null],
        [t.mNotice, c.terminationMonths ? t.months(c.terminationMonths) : null]
      ].filter(([, v]) => v) as [string, string][])
    : [];
  return (
    <section class="blk" aria-labelledby="cc-monday">
      <h2 class="blk-title" id="cc-monday">
        {t.monday}
      </h2>
      {rows.length ? (
        <dl class="cc-company small">
          {rows.map(([k, v]) => (
            <Fragment key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </Fragment>
          ))}
        </dl>
      ) : null}
      {files.length ? (
        <ul class="cc-files small" aria-label={t.mFiles}>
          {files.map((f) => (
            <li key={f.id}>
              {f.url ? (
                <a href={f.url} target="_blank" rel="noopener noreferrer">
                  {f.name}
                </a>
              ) : (
                <span>{f.name}</span>
              )}
              <span class="cell-sub">
                {[f.at?.slice(0, 10), f.source === 'note' ? t.mNoteFile : null, f.state === 'waiting' ? t.mWaiting : f.state === 'failed' ? t.mFailed : null].filter(Boolean).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** The client card: every note from the client and its projects, with the projects and contacts beside them. */
export function ClientCard(p: { api: Api; id: string }) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<ClientCardView | null>(() => peek(p.api, `/api/clients/${p.id}/card`) ?? null);
  const [err, setErr] = useState<unknown>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<EditableContact | null>(null);
  const load = () => p.api.get<ClientCardView>(`/api/clients/${p.id}/card`).then(setV, setErr);
  useEffect(() => {
    void load();
  }, [p.id]);
  if (err) return <LoadError error={err} />;
  if (!v) return <Loading />;
  const open = v.projects.filter((x) => x.status !== 'closed');
  return (
    <div class="screen">
      <p>
        <a class="back-link" href="/clients" onClick={go('/clients')}>
          {t.backClients}
        </a>
      </p>
      <header class="phead">
        <p class="pj-eyebrow">
          {t.client}
          {t.lifecycle[v.client.lifecycle ?? ''] ? ` · ${t.lifecycle[v.client.lifecycle ?? '']}` : ''}
        </p>
        <h1 class="h1" title={v.client.code}>
          {v.client.name}
        </h1>
        <p class="sub">
          {t.projects(v.projects.length)}
          {open.length !== v.projects.length ? t.nOpen(open.length) : ''} · {t.nNotes(v.notes.length)}
        </p>
      </header>
      {v.facts && v.today ? <ClientFactsBand facts={v.facts} today={v.today} contacts={v.contacts.length} briefWritten={Boolean(v.brief.body.trim())} newQuote={v.can?.quote ? `/agreements/new?client=${v.client.id}` : undefined} /> : null}
      {v.start ? <ClientStartBlock api={p.api} clientId={v.client.id} s={v.start} canEdit={Boolean(v.can?.contacts)} onChanged={() => void load()} /> : null}
      <div class="cc">
        <div class="cc-main stack">
          <ClientBriefBlock api={p.api} clientId={v.client.id} brief={v.brief} onSaved={(brief) => setV({ ...v, brief })} />
          <Composer api={p.api} target={{ clientId: v.client.id, projects: v.projects }} people={v.people} onAdded={(n) => setV({ ...v, notes: ordered([n, ...v.notes]) })} />
          <NoteFeed
            api={p.api}
            notes={v.notes}
            setNotes={(notes) => setV({ ...v, notes })}
            people={v.people}
            showProject
            projects={v.projects.filter((x) => v.notes.some((n) => n.project?.id === x.id))}
            empty={t.emptyClient}
          />
        </div>
        <aside class="cc-side stack" aria-label={t.projectsContacts}>
          <ClientBrandBlock api={p.api} clientId={v.client.id} />
          <section class="blk" aria-labelledby="cc-p">
            <h2 class="blk-title" id="cc-p">
              {t.projectsHead}
            </h2>
            {v.projects.length ? (
              <ul class="cc-projects">
                {v.projects.map((x) => (
                  <li>
                    <a href={`/projects/${x.id}`} onClick={go(`/projects/${x.id}`)} title={x.code}>
                      {x.name}
                    </a>
                    <span class="small muted">
                      {monthLabel(x.deliveryMonth, lang)}
                    </span>
                    <StageBar stage={x.stage} compact />
                  </li>
                ))}
              </ul>
            ) : (
              <p class="muted">{t.noProjects}</p>
            )}
          </section>
          {v.history?.length ? <EarlierProjects rows={v.history} t={t} lang={lang} /> : null}
          <section class="blk" aria-labelledby="cc-c">
            <div class="blk-head">
              <h2 class="blk-title" id="cc-c">
                {t.contacts}
              </h2>
              {v.can?.contacts ? (
                <button type="button" class="btn btn-sm" onClick={() => setAdding(true)}>
                  {t.addContact}
                </button>
              ) : null}
            </div>
            {v.can?.contacts && v.portalSuggest?.length ? (
              <PortalInviteSuggestion api={p.api} clientId={v.client.id} contacts={v.contacts.filter((c) => v.portalSuggest!.includes(c.id))} onDone={() => void load()} />
            ) : null}
            {v.contacts.length ? (
              <ul class="cc-contacts">
                {v.contacts.map((c) => (
                  <li>
                    <strong>{c.name ?? c.email}</strong>
                    <span class="small muted">
                      {c.title ? `${c.title} · ` : ''}
                      {c.name ? c.email : ''}
                      {c.phone ? ` · ${c.phone}` : ''}
                    </span>
                    {v.can?.contacts ? (
                      <button type="button" class="btn btn-ghost btn-sm cc-contact-edit" aria-label={t.editContactFor(c.name ?? c.email)} onClick={() => setEditing(c)}>
                        {t.editContact}
                      </button>
                    ) : null}
                    {c.portal ? <PortalAccessLine access={c.portal} /> : null}
                    {v.can?.contacts && c.portal ? (
                      <span class="cc-contact-invite">
                        <InviteButton api={p.api} clientId={v.client.id} contact={c} access={c.portal} onDone={() => void load()} />
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p class="muted">{t.noContacts}</p>
            )}
          </section>
          {v.client.orgNo || v.client.address || v.client.invoiceInfo ? (
            <section class="blk" aria-labelledby="cc-co">
              <h2 class="blk-title" id="cc-co">
                {t.company}
              </h2>
              <dl class="cc-company small">
                {v.client.orgNo ? (
                  <>
                    <dt>{t.orgNo}</dt>
                    <dd>{v.client.orgNo}</dd>
                  </>
                ) : null}
                {v.client.address ? (
                  <>
                    <dt>{t.address}</dt>
                    <dd>{v.client.address}</dd>
                  </>
                ) : null}
                {v.client.invoiceInfo ? (
                  <>
                    <dt>{t.invoice}</dt>
                    <dd>{v.client.invoiceInfo}</dd>
                  </>
                ) : null}
              </dl>
            </section>
          ) : null}
          {v.monday ? <MondayAgreement m={v.monday} t={t} /> : null}
          <ClientMailLanguage
            api={p.api}
            clientId={v.client.id}
            language={v.client.language}
            mail={v.client.clientMail ?? 'on'}
            canEdit={Boolean(v.can?.contacts)}
            onSaved={(language) => setV({ ...v, client: { ...v.client, language } })}
            onMail={(clientMail) => setV({ ...v, client: { ...v.client, clientMail } })}
          />
          <ClientDriveFolders api={p.api} clientId={v.client.id} canMake={Boolean(v.can?.contacts)} />
          <ClientFilms api={p.api} clientId={v.client.id} />
          <ClientBillingBlock api={p.api} clientId={v.client.id} />
        </aside>
      </div>
      {adding ? <ContactDialog api={p.api} clientId={v.client.id} onClose={() => setAdding(false)} onAdded={() => void load()} /> : null}
      {editing ? <ContactDialog api={p.api} contact={editing} onClose={() => setEditing(null)} onAdded={() => void load()} /> : null}
    </div>
  );
}

/** The language of everything the Hub sends the client (owner 2026-10-19): one tick for English, Swedish otherwise. */
type ClientMailLevel = 'on' | 'no_reminders' | 'off';

/**
 * The block "Emails to the client" on the client card: the language (owner 2026-10-19) and how much the Hub emails the
 * client about review and delivery (owner 2026-10-24).
 */
function ClientMailLanguage(p: { api: Api; clientId: string; language: 'sv' | 'en'; mail: ClientMailLevel; canEdit: boolean; onSaved: (l: 'sv' | 'en') => void; onMail: (m: ClientMailLevel) => void }) {
  const t = TX[useLang()];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const change = async (en: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const r = await p.api.put<{ client: { language: 'sv' | 'en' } }>(`/api/clients/${p.clientId}/language`, { language: en ? 'en' : 'sv' });
      p.onSaved(r.client.language);
      toast({ text: t.mailSaved(r.client.language === 'en') });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const how = async (mail: ClientMailLevel) => {
    setBusy(true);
    setError(null);
    try {
      const r = await p.api.put<{ client: { clientMail?: ClientMailLevel } }>(`/api/clients/${p.clientId}/client-mail`, { mail });
      p.onMail(r.client.clientMail ?? mail);
      toast({ text: t.mailHowSaved });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section class="blk" aria-labelledby="cc-m">
      <h2 class="blk-title" id="cc-m">
        {t.mailTitle}
      </h2>
      <label class="check">
        <input type="checkbox" checked={p.language === 'en'} disabled={busy || !p.canEdit} onChange={(e) => void change((e.target as HTMLInputElement).checked)} aria-describedby="cc-m-hint" /> {t.mailEnglish}
      </label>
      <p class="small muted" id="cc-m-hint">
        {t.mailHint}
      </p>
      <label class="field" for="cc-m-how">
        <span>{t.mailHow}</span>
        <select id="cc-m-how" class="input" style="width:100%" value={p.mail} disabled={busy || !p.canEdit} onChange={(e) => void how((e.currentTarget as HTMLSelectElement).value as ClientMailLevel)} aria-describedby="cc-m-how-hint">
          {(['on', 'no_reminders', 'off'] as const).map((k) => (
            <option value={k}>{t.mailOpt[k]}</option>
          ))}
        </select>
      </label>
      <p class="small muted" id="cc-m-how-hint">
        {t.mailHowHint}
      </p>
      {error ? <p class="field-error">{error}</p> : null}
    </section>
  );
}

interface ClientFolders {
  on: boolean;
  state: 'none' | 'pending' | 'ready' | 'rename_blocked' | 'failed';
  url: string | null;
  error: string | null;
}

/** The client's Drive folders (owner 2026-10-19): open them, or have the Hub make (or find) them now. */
function ClientDriveFolders(p: { api: Api; clientId: string; canMake: boolean }) {
  const t = TX[useLang()];
  const [f, setF] = useState<ClientFolders | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    p.api.get<ClientFolders>(`/api/clients/${p.clientId}/drive-folders`).then(setF, () => setF(null));
  }, [p.clientId]);
  if (!f?.on) return null;
  const make = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await p.api.post<ClientFolders & { error?: string | null }>(`/api/clients/${p.clientId}/drive-folders`);
      setF(r);
      if (r.error) setError(r.error);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section class="blk" aria-labelledby="cc-d">
      <h2 class="blk-title" id="cc-d">
        {t.driveTitle}
      </h2>
      {f.url ? (
        <p class="cc-drive">
          <span class="small muted">{t.driveReady}</span>
          <a class="btn btn-sm" href={f.url} target="_blank" rel="noopener noreferrer">
            {t.driveOpen}
          </a>
        </p>
      ) : (
        <>
          <p class="small muted">{f.state === 'failed' ? f.error : t.driveWait}</p>
          {p.canMake ? (
            <button type="button" class="btn btn-sm" disabled={busy} onClick={() => void make()}>
              {busy ? t.driveMaking : t.driveMake}
            </button>
          ) : null}
        </>
      )}
      {error ? <p class="small warn" role="alert">{error}</p> : null}
    </section>
  );
}

/** My Work: notes from the last 30 days where a colleague @-named you. */
export function MentionsOnNow(p: { api: Api }) {
  const lang = useLang();
  const t = TX[lang];
  const [notes, setNotes] = useState<NoteView[] | null>(null);
  useEffect(() => {
    p.api.get<{ notes: NoteView[] }>('/api/notes/mentions').then(
      (r) => setNotes(r.notes),
      () => setNotes([])
    );
  }, []);
  if (!notes?.length) return null;
  return (
    <section class="blk" aria-labelledby="mw-mentions">
      <h2 class="blk-title" id="mw-mentions">
        {t.mentioned}
      </h2>
      <ul class="notes notes--compact">
        {notes.slice(0, 5).map((n) => {
          const href = n.project ? `/projects/${n.project.id}/notes` : `/clients/${n.client.id}`;
          return (
            <li class="note">
              <a class="note-mention-link" href={href} onClick={go(href)}>
                <span class="small muted">
                  {n.author.name} · {when(n.createdAt, lang)} · {n.project ? n.project.name : n.client.name}
                </span>
                <span class="note-excerpt">{n.body.length > 140 ? `${n.body.slice(0, 140)}…` : n.body}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
